import type {
  DefaultError,
  InfiniteData,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useLayoutEffect, useMemo, useRef } from "react";

import { NotImplementedError } from "@citrine/utils/errors";

import { QueryClient } from "./query-client";
import { useRevertableQuery } from "./use-revertable-query";
import type { IQuery } from "./utils";

const structuralSharing = (oldData: any | undefined, newData: any) => {
  return oldData && JSON.stringify(oldData) === JSON.stringify(newData)
    ? oldData
    : newData;
};

export abstract class InfiniteClient<
  Model,
  Identifier = string,
  QueryParams extends Record<string, any> = IQuery,
> extends QueryClient<Model, Identifier, QueryParams> {
  async findAll(query?: QueryParams): Promise<Model[]> {
    const results: Model[] = [];

    let nextQuery: IQuery | undefined = {};
    while (nextQuery) {
      const page = await this.find({
        ...query,
        ...nextQuery,
      } as QueryParams);
      results.push(...page);

      const per_page = Number(query?.per_page ?? this.defaultQuery?.per_page);

      if (
        !page.length ||
        (per_page && page.length < per_page) ||
        results.length >= 4000
      ) {
        break;
      }

      nextQuery = this.getNextQuery(page);
    }

    return results;
  }

  /**
   * This operates the same as useFind but it will return all results that are
   * found in an eager load
   *
   * @param {QueryParams} query
   * @param {UseQueryOptions<Model[]>} options
   * @returns {UseQueryResult<Model[]>}
   */
  useFindAll<Data = Model[]>(
    query?: QueryParams,
    options: Omit<
      UseQueryOptions<Model[], AxiosError, Data>,
      "queryKey" | "queryFn"
    > = {},
  ) {
    return useRevertableQuery<Model[], AxiosError, Data>({
      ...options,
      queryKey: [...this.getQueryCacheKey(query), "all"],
      queryFn: () => this.findAll(query),
      enabled: Boolean((options.enabled ?? true) && this.isEnabled()),
    });
  }

  /**
   * wrapper for react-query useInfiniteQuery that returns a list of models by search query
   * https://react-query.tanstack.com/reference/useInfiniteQuery
   *
   * const result = client.useInfinite();
   * if (result.isSuccess) {
   *  result.data.map((model) => { console.log(model.id); });
   * }
   *
   * // this is a simple example of the API, but this is very bad; don't use this
   * const onScroll = useCallback(() => {
   *  if (result.canFetchMore) {
   *    result.fetchMore();
   *  }
   * }, [result.canFetchMore, result.fetchMore]);
   *
   * @param {QueryParams} query
   * @param {UseInfiniteQueryOptions<Model[]>} options
   * @returns {UseInfiniteQueryResult<Model[]>}
   */
  useInfinite(
    query?: QueryParams,
    options: Omit<
      UseInfiniteQueryOptions<Model[], DefaultError, InfiniteData<Model[]>>,
      "queryKey" | "queryFn" | "getNextPageParam"
    > = { initialPageParam: {} },
  ): UseInfiniteQueryResult<InfiniteData<Model[]>> {
    const result = useInfiniteQuery({
      ...options,
      queryKey: [...this.getQueryCacheKey(query), "infinite"],
      queryFn: ({ pageParam }) => {
        return this.find(
          pageParam
            ? query
              ? { ...query, ...pageParam }
              : (pageParam as any as QueryParams)
            : query,
        );
      },
      // structural sharing will clear our symbols; must be false
      structuralSharing,
      getNextPageParam: (
        lastPage: Model[],
        allPages: Model[][],
      ): IQuery | undefined => {
        // if the latest response was empty or has fewer entries than the
        // previous response, then we know we're done
        if (
          // latest response was not empty
          lastPage.length > 0 &&
          // latest response was the first response
          (allPages.length <= 1 ||
            // latest response has same length as previous response
            lastPage.length === allPages[allPages.length - 2].length)
        ) {
          return this.getNextQuery(lastPage);
        }
      },
      enabled: Boolean((options.enabled ?? true) && this.isEnabled()),
    });

    useLayoutEffect(() => {
      if (result.data?.pages.length) {
        const models = result.data.pages[result.data.pages.length - 1];
        this.addModelsToCache(models);
      }
    }, [result.data]);

    return result;
  }

  /**
   * This operates the same as useMap but it will return all results that are
   * found in an eager load
   *
   * @param {QueryParams} query
   * @param {UseQueryOptions<Model[]>} options
   * @returns {UseQueryResult<Partial<Record<string, Model>>>}
   */
  useMapAll(
    query?: QueryParams,
    options: Omit<
      UseQueryOptions<Model[], AxiosError>,
      "queryKey" | "queryFn"
    > = {},
  ): UseQueryResult<Partial<Record<string, Model>>> {
    const response = this.useFindAll(query, options);

    const responseRef = useRef(response);
    responseRef.current = response;

    return useMemo(
      () =>
        ({
          ...responseRef.current,
          status: response.status,
          data: response.data && this.getLookupMap(response.data),
        }) as any,
      [response.status, response.data],
    );
  }

  /**
   * for paging/infinite queries, extract the next query from the previous payload
   * @param {Object} previousPayload
   * @returns {IQuery | undefined}
   */
  protected getNextQuery(_previousPayload: Model[]): IQuery | undefined {
    throw new NotImplementedError();
  }
}

export default InfiniteClient;
